import { useLazyQuery, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { GenerateSharedAccessSignatureQuery } from '@deltek/specification-client/core/GraphQL/Queries/generateSharedAccessSignature.queries'
import {
    GenerateSharedAccessSignatureInput,
    GenerateSharedAccessSignatureResponse,
} from '@deltek/specification-client/core/Models/generateSharedAccessSignature'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { CdnClientUrl } from '@deltek/specification-client/core/Utilities/urlUtils'
import {
    PickerFileMetadata,
    PickerResponse,
} from '@deltek/specification-client/core/Widgets/Filestack/FilestackButton'
import ImageFilestackButton from '@deltek/specification-client/core/Widgets/Filestack/ImageFilestackButton'
import Backdrop from '@material-ui/core/Backdrop'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardMedia from '@material-ui/core/CardMedia'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Theme, makeStyles } from '@material-ui/core/styles'
import Skeleton from '@material-ui/lab/Skeleton'
import { Project } from 'Models/project'
import {
    ResetProjectImageMutation,
    UploadProjectImageMutation,
} from 'api/graphQL/mutations/project.mutations'
import { processEnv } from 'components/configuration/environmentVariables'
import {
    ResetProjectImageInput,
    UploadProjectImageInput,
} from 'models/projectInput'
import {
    cardProjectImageDimension,
    determineProjectImage,
    projectOverviewImageDimension,
} from 'pages/ProjectsPage/components/projectImageUtil'
import React, { useCallback, useEffect, useState } from 'react'
import ProjectOverviewImageCardMenu from './ProjectOverviewImageCardMenu'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        boxShadow: 'none',
        position: 'relative',
        width: '100%',
        height: 325,
        borderRadius: 0,
    },
    content: {
        position: 'absolute',
        zIndex: 2,
        bottom: 0,
        width: '100%',
        background:
            'linear-gradient(89.99deg, #A78966 0.01%, rgba(0, 0, 0, 0) 99.58%)',
        height: 70,
        display: 'flex',
        alignItems: 'center',
    },
    contentText: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: pxToRem(48),
        color: '#fff',
    },
    media: {
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        position: 'absolute',
    },
    header: {
        color: theme.palette.common.white,
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: pxToRem(24),
        paddingBottom: 0,
        paddingTop: theme.spacing(2),
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
}))

interface ProjectOverviewImageCardProps {
    variant?: 'readOnly' | 'full'
    projectData: Partial<Project> | undefined
}

export const ProjectOverviewImageCard: React.FC<ProjectOverviewImageCardProps> = ({
    variant,
    projectData,
}) => {
    const classes = useStyles()

    const [
        uploadProjectImage,
        {
            loading: uploadProjectImageLoading,
            error: uploadProjectImageError,
            data: uploadProjectImageData,
        },
    ] = useMutation<
        { uploadProjectImage: Partial<Project> | null },
        { uploadInput: UploadProjectImageInput }
    >(UploadProjectImageMutation)

    const [
        resetProjectImage,
        {
            loading: resetProjectImageLoading,
            error: resetProjectImageError,
            data: resetProjectImageData,
        },
    ] = useMutation<
        { resetProjectImage: Partial<Project> | null },
        { resetProjectInput: ResetProjectImageInput }
    >(ResetProjectImageMutation)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [filestackLoading, setFilestackLoading] = useState<boolean>(false)
    const [projectImageInTransit, setProjectImageInTransit] = React.useState<
        boolean
    >(false)
    const [imageFile, setImageFile] = useState<PickerFileMetadata>()
    const [fullImagePath, setFullImagePath] = useState<string>()

    const [showResetImageMenu, setShowResetImageMenu] = useState<boolean>(false)
    const [computedImageSrc, setComputedImageSrc] = useState<string | null>(
        null
    )
    const [
        generateSharedAccessSignature,
        { loading: sasLoading, error: sasError, data: sasData },
    ] = useLazyQuery<
        GenerateSharedAccessSignatureResponse,
        GenerateSharedAccessSignatureInput
    >(GenerateSharedAccessSignatureQuery)

    const onFilepickerDone = (pickerResponse: PickerResponse) => {
        if (pickerResponse.filesFailed.length) {
            showGlobalSnackbar?.({
                variables: {
                    id: 'ProjectImageFileUploadError',
                    message: 'Error uploading project image.',
                    variant: 'error',
                },
            })
            return
        }

        setComputedImageSrc('')
        generateSharedAccessSignature({
            variables: {
                input: {
                    durationInMinutes: 5,
                    storageAccountName:
                        processEnv.REACT_APP_BlobStorageAccountName,
                },
            },
        })
        setImageFile(pickerResponse.filesUploaded[0])
    }

    const handleImageFileInTransitChange = (inTransit: boolean) => {
        setProjectImageInTransit(inTransit)
        if (inTransit) setComputedImageSrc('')
    }
    /**
     * This is a workaround for the filestack
     * "onOpen" delay. It starts a poll for the presence of
     * the filestack picker modal in the DOM, and sets
     * the filestackLoading state to false once the
     * element is found.
     */
    const checkPickerUi = () => {
        const hasPickerUi =
            document.getElementsByClassName('fsp-modal').length >= 1
        if (hasPickerUi) {
            setFilestackLoading(false)
        } else {
            setTimeout(() => checkPickerUi(), 500)
        }
    }
    const handleFilestackLoading = (isLoading: boolean | null) => {
        if (isLoading === false) {
            // Cover the "gap" by starting a poll
            // for the picker modal element in the DOM.
            checkPickerUi()
        } else {
            setFilestackLoading(true)
        }
    }

    const handleRestoreDefault = () => {
        resetProjectImage({
            variables: {
                resetProjectInput: {
                    projectId: projectData?.id,
                    firmId: projectData?.firmId,
                } as ResetProjectImageInput,
            },
        }).catch(() => {
            // This is a temporary workaround that will be removed once a global
            // GraphQL error handler is implemented.
        })
    }

    const decideProjectImageUrl = useCallback(
        (projectImageUrl: string | null | undefined) => {
            return determineProjectImage({
                imageUrl: projectImageUrl,
                parameters: projectData?.parameters,
                imageDimension: projectOverviewImageDimension,
                defaultImage: `${CdnClientUrl}/placeholder_project_overview_image.png`,
            })
        },
        [projectData]
    )

    useEffect(() => {
        if (!imageFile || !sasData) return
        const imagePath = `${processEnv.REACT_APP_BlobStorageUrl}/${
            processEnv.REACT_APP_BlobStorageUnverifiedContainerName
        }/${encodeURIComponent(imageFile.key!)}${
            sasData.generateSharedAccessSignature.sharedAccessSignature
        }`
        setFullImagePath(imagePath)
        uploadProjectImage({
            variables: {
                uploadInput: {
                    projectId: projectData?.id,
                    firmId: projectData?.firmId,
                    image: {
                        fileName: imageFile.filename,
                        key: imageFile.key!,
                        fileSize: imageFile.size,
                        resizeDimensions: [
                            projectOverviewImageDimension,
                            cardProjectImageDimension,
                        ],
                    },
                } as UploadProjectImageInput,
            },
        }).catch(() => {
            // This is a temporary workaround that will be removed once a global
            // GraphQL error handler is implemented.
        })
    }, [imageFile, sasData, uploadProjectImage, projectData])

    useEffect(() => {
        if (fullImagePath) {
            setComputedImageSrc(fullImagePath)
            return
        }
    }, [fullImagePath])

    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (uploadProjectImageError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        uploadProjectImageError,
                        'Error in uploading project image.'
                    ),
                    variant: 'error',
                    id: 'projectImageUploadError',
                },
            })
        }
        if (resetProjectImageError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        resetProjectImageError,
                        'Error in restoring default project image.'
                    ),
                    variant: 'error',
                    id: 'projectImageRestoreDefaultImageError',
                },
            })
        }

        if (sasError) {
            showGlobalSnackbar({
                variables: {
                    message: summarizeApolloError(
                        sasError,
                        'Error getting shared access signature.'
                    ),
                    variant: 'error',
                    id: 'projectImageSASError',
                },
            })
        }
    }, [
        uploadProjectImageError,
        resetProjectImageError,
        sasError,
        showGlobalSnackbar,
    ])

    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (
            !!uploadProjectImageData &&
            !!uploadProjectImageData.uploadProjectImage!.imageUrl
        ) {
            showGlobalSnackbar({
                variables: {
                    message: `Project image uploaded successfully.`,
                    variant: 'success',
                    id: `ProjectImageUploadSuccess`,
                },
            })
            setComputedImageSrc(
                decideProjectImageUrl(
                    uploadProjectImageData.uploadProjectImage!.imageUrl
                )!
            )
            setShowResetImageMenu(true)
        }
    }, [uploadProjectImageData, showGlobalSnackbar, decideProjectImageUrl])

    React.useEffect(() => {
        if (!!projectData) {
            setComputedImageSrc(decideProjectImageUrl(projectData.imageUrl)!)
            if (!!projectData.imageUrl) {
                setShowResetImageMenu(true)
            }
        }
    }, [projectData, decideProjectImageUrl])

    React.useEffect(() => {
        if (!showGlobalSnackbar) return

        if (
            !!resetProjectImageData &&
            !!resetProjectImageData.resetProjectImage
        ) {
            showGlobalSnackbar({
                variables: {
                    message: `Project image restored to default.`,
                    variant: 'success',
                    id: `ProjectImageUploadSuccess`,
                },
            })

            setComputedImageSrc(decideProjectImageUrl(null)!)
            setShowResetImageMenu(false)
        }
    }, [
        resetProjectImageData,
        projectData,
        showGlobalSnackbar,
        decideProjectImageUrl,
    ])

    return (
        <>
            <Card className={classes.card} data-testid="dvProjectImageCard">
                <CardHeader
                    className={classes.header}
                    action={
                        variant === 'full' && (
                            <ImageFilestackButton
                                apiKey={processEnv.REACT_APP_FileStack_ApiKey}
                                kpCoreUri={processEnv.REACT_APP_KPCoreUri}
                                cropDimension={[
                                    projectOverviewImageDimension.width!,
                                    projectOverviewImageDimension.height!,
                                ]}
                                onFilepickerDone={onFilepickerDone}
                                showGlobalSnackbar={showGlobalSnackbar}
                                handleLoadingFilestack={handleFilestackLoading}
                                handleTransferringFiles={
                                    handleImageFileInTransitChange
                                }
                                renderButton={(
                                    onClick: (event: React.MouseEvent) => void
                                ) => (
                                    <ProjectOverviewImageCardMenu
                                        onRestoreDefaultClick={
                                            handleRestoreDefault
                                        }
                                        onUploadImageClck={onClick}
                                        showRestoreDefault={showResetImageMenu}
                                    />
                                )}
                            />
                        )
                    }
                />
                {(projectImageInTransit ||
                    sasLoading ||
                    uploadProjectImageLoading ||
                    resetProjectImageLoading ||
                    !computedImageSrc) && (
                    <Skeleton
                        data-testid="projectImageEditSkeleton"
                        variant="rect"
                        animation="wave"
                        height="100%"
                        className={classes.media}
                    />
                )}
                {!projectImageInTransit &&
                    !sasLoading &&
                    !uploadProjectImageLoading &&
                    !resetProjectImageLoading &&
                    !!computedImageSrc && (
                        <CardMedia
                            data-testid="projectImage"
                            className={classes.media}
                            image={computedImageSrc}
                        />
                    )}
                <Box py={3} px={2} className={classes.content}>
                    <div className={classes.contentText}>Project Overview</div>
                </Box>
            </Card>
            <Backdrop className={classes.backdrop} open={filestackLoading}>
                {!filestackLoading && <CircularProgress color="inherit" />}
            </Backdrop>
        </>
    )
}
export default ProjectOverviewImageCard
